/**
 * Type guard to check if a value is one of the allowed values
 */
const isOneOf = <A extends readonly string[], V extends string>(
  arr: A,
  x: V
): x is Extract<V, A[number]> => arr.includes(x);

/**
 * Validates a single string value against a list of valid values
 */
export const validateSingleValue = <T extends readonly string[]>(
  value: unknown,
  validValues: T
): T[number] | undefined => {
  if (typeof value === 'string' && isOneOf(validValues, value)) {
    return value;
  }
  return undefined;
};

/**
 * Parses and validates comma-separated values
 */
export const parseAndValidateCommaSeparatedValues = <T extends readonly string[]>(
  value: unknown,
  validValues: T
): T[number][] => {
  if (typeof value !== 'string') return [];

  return value
    .split(',')
    .map((v) => v.trim())
    .filter((v): v is T[number] => typeof v === 'string' && isOneOf(validValues, v));
};

/**
 * Helper to conditionally add a property to an object if the value exists
 */
export const addPropertyIfValid = <T, K extends keyof T>(
  object: T,
  key: K,
  value: T[K] | undefined
): T => {
  if (value === undefined) return object;

  return {
    ...object,
    [key]: value,
  };
};

/**
 * Helper to conditionally add an array property to an object if the array has items
 */
export const addArrayPropertyIfNotEmpty = <T, K extends keyof T>(
  object: T,
  key: K,
  value: T[K] & unknown[]
): T => {
  if (!value || value.length === 0) return object;

  return {
    ...object,
    [key]: value,
  };
};
